import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Field } from "@/components/accounts/shared";
import { ClientsPanel, PortfolioPanel, TeamPanel } from "@/components/ca/FirmPanels";
import { TasksPanel, DocsPanel } from "@/components/ca/WorkPanels";

function CreateFirm({ onDone }) {
  const [f, setF] = useState({ name: "", registration_no: "", city: "" });
  const save = async () => { try { await api.post("/ca/firm", f); toast.success("Firm workspace created"); onDone(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="max-w-lg space-y-3" testid="ca-create-firm">
      <p className="font-semibold">Set up your CA firm workspace</p>
      <p className="text-sm text-slate-500">You become the firm's first partner. Clients authorise your firm using its firm code; you never get access just by knowing a client's ID.</p>
      <Field label="Firm name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ca-firm-name" /></Field>
      <Field label="Registration no. (optional)"><Input value={f.registration_no} onChange={(e) => setF({ ...f, registration_no: e.target.value })} data-testid="ca-firm-reg" /></Field>
      <Field label="City (optional)"><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
      <Button disabled={f.name.trim().length < 2} onClick={save} data-testid="ca-firm-create">Create firm</Button>
    </Card>
  );
}

const TABS = [["clients", "Clients"], ["tasks", "Review queue"], ["docs", "Document requests"], ["portfolio", "Portfolio"], ["team", "Team"]];

export default function CaWorkspace() {
  const { data: me, refetch } = useQuery({ queryKey: ["ca-me"], queryFn: async () => (await api.get("/ca/me")).data });
  const role = me?.membership?.ca_role;
  const { data: team, refetch: reTeam } = useQuery({ queryKey: ["ca-team"], enabled: !!role, queryFn: async () => (await api.get("/ca/firm/members")).data });
  const { data: cl } = useQuery({ queryKey: ["ca-clients"], enabled: !!role, queryFn: async () => (await api.get("/ca/firm/clients")).data });
  const [tab, setTab] = useState("clients");
  if (!me) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!role) {
    return (
      <div data-testid="ca-workspace">
        <PageHeader title="CA Firm Workspace" subtitle="Review authorised client businesses" />
        {me.can_create_firm ? <CreateFirm onDone={refetch} /> : <Card testid="ca-not-member"><p className="text-sm text-slate-600">You're not a member of a CA firm. Ask your firm's partner to add you.</p></Card>}
      </div>
    );
  }
  const clients = (cl?.items || []).filter((c) => c.status === "active");
  const members = team?.members || [];
  return (
    <div data-testid="ca-workspace">
      <PageHeader title={me.firm.name} subtitle={`CA Firm Workspace · you are ${role.replace("_", " ")}`} />
      <p className="-mt-2 mb-3 text-xs text-slate-500" data-testid="ca-firm-code-display">Firm code for client authorisation: <b className="font-mono">{me.firm.firm_code}</b></p>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
        {TABS.filter(([k]) => k !== "team" || role === "partner").map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ca-tab-${k}`}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>))}
      </div>
      {tab === "clients" && <ClientsPanel role={role} members={members} />}
      {tab === "tasks" && <TasksPanel role={role} clients={clients} />}
      {tab === "docs" && <DocsPanel clients={clients} />}
      {tab === "portfolio" && <PortfolioPanel />}
      {tab === "team" && team && <TeamPanel data={team} refetch={reTeam} />}
    </div>
  );
}
